"use client";

import { memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { apiAuthFetch } from "@/lib/authFetch";
import { Spinner } from "@/components/ui/spinner";
import { getPdfZoom } from "@/lib/storage";

// A4 height-to-width ratio used to simulate one "page" of content for fit-page.
const A4_RATIO = 297 / 210;

/**
 * Compute the zoom value that makes one "A4-equivalent page" of diff content
 * fit inside the container. Caller must reset zoom to "1" before calling so
 * that scrollWidth reflects the natural (unscaled) document size.
 */
function computeDiffFitPage(iframe: HTMLIFrameElement): number {
  const container = iframe.parentElement;
  if (!container) return 1;
  const naturalWidth = iframe.contentDocument?.documentElement.scrollWidth ?? 0;
  if (naturalWidth === 0) return 1;
  const containerW = container.clientWidth;
  const containerH = container.clientHeight;
  const pageHeight = naturalWidth * A4_RATIO;
  return Math.min(containerW / naturalWidth, containerH / pageHeight);
}

/**
 * Compute the zoom value that makes the diff content fill the container width.
 * Caller must reset zoom to "1" first.
 */
function computeDiffFitWidth(iframe: HTMLIFrameElement): number {
  const container = iframe.parentElement;
  if (!container) return 1;
  const naturalWidth = iframe.contentDocument?.documentElement.scrollWidth ?? 0;
  if (naturalWidth === 0) return 1;
  return container.clientWidth / naturalWidth;
}

/**
 * Apply a numeric zoom to the diff iframe content.
 */
export function applyDiffZoom(iframe: HTMLIFrameElement, zoom: number): void {
  if (iframe.contentDocument?.documentElement) {
    iframe.contentDocument.documentElement.style.zoom = String(zoom);
  }
}

/**
 * Diff renderer that takes the slot the PDF viewer normally occupies. Reads the
 * cached diff HTML from <see cref="usePdfDocumentStore"/> keyed by versionId, and
 * lazy-fetches it on first mount when no cache exists.
 *
 * Render strategy: a sandboxed iframe via <c>srcdoc</c> with <c>allow-same-origin</c>
 * but <b>not</b> <c>allow-scripts</c>. The LDOM diff is HTML+CSS only, so we don't
 * need scripts and disabling them prevents the LLM-shaped (or attacker-shaped) diff
 * from running JS that could read DOM state from the parent. Same-origin lets the
 * iframe inherit the parent's CSS styling baseline if needed, and also lets the
 * parent modify contentDocument for zoom and DragDropGuard.
 */
export const PdfDiffView = memo(function PdfDiffView({
  docId,
  versionId,
  iframeRef,
  onZoomChange,
}: {
  docId: string;
  versionId?: string;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
  onZoomChange?: (zoom: number) => void;
}) {
  const { t } = useTranslation();
  // Resolve the version we render the diff for: explicit prop wins, then the
  // entry's selected version, then current, then the latest in the list.
  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);
  const resolvedVersionId =
    versionId
    ?? entry?.selectedVersionId
    ?? entry?.currentVersionId
    ?? entry?.versions?.at(-1)?.versionId;

  const cachedHtml = usePdfDocumentStore((s) =>
    resolvedVersionId ? s.diffHtmlByVersion[resolvedVersionId] : undefined,
  );

  // Lazy-fetch on first toggle into Diff for this (docId, versionId). The cache
  // is per-versionId, so flipping between versions does NOT invalidate the
  // already-fetched diff for the version we just left.
  useEffect(() => {
    if (!resolvedVersionId) return;
    if (cachedHtml !== undefined) return;
    let cancelled = false;

    // Check if this is a pending version with a direct preview diff URL.
    const resolvedVersion = entry?.versions?.find((v) => v.versionId === resolvedVersionId);
    const fetchUrl = resolvedVersion?.isPending && resolvedVersion.previewDiffHtmlUrl
      ? resolvedVersion.previewDiffHtmlUrl
      : `/api/documents/${docId}/diff?version=${resolvedVersionId}`;

    (async () => {
      try {
        const res = await apiAuthFetch(fetchUrl);
        if (cancelled) return;
        if (!res.ok) {
          usePdfDocumentStore.getState().setDiffHtml(resolvedVersionId, null);
          return;
        }
        const html = await res.text();
        if (cancelled) return;
        usePdfDocumentStore.getState().setDiffHtml(resolvedVersionId, html);
      } catch {
        if (cancelled) return;
        usePdfDocumentStore.getState().setDiffHtml(resolvedVersionId, null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId, resolvedVersionId, cachedHtml, entry?.versions]);

  // Apply initial zoom and bridge DragDropGuard once the iframe document loads.
  const handleLoad = useCallback(() => {
    const iframe = iframeRef?.current;
    if (!iframe || !iframe.contentDocument || !iframe.contentWindow) return;

    // Bridge DragDropGuard: the parent's window listener doesn't cover the
    // iframe's window. Mirror the same "swallow unhandled drags" logic here.
    const swallow = (e: DragEvent) => {
      if (!e.defaultPrevented) e.preventDefault();
    };
    iframe.contentWindow.addEventListener("dragover", swallow);
    iframe.contentWindow.addEventListener("drop", swallow);
    // No cleanup needed — the listeners die with the iframe's window context.

    // Reset to natural size before measuring so scrollWidth is accurate.
    iframe.contentDocument.documentElement.style.zoom = "1";

    const saved = getPdfZoom();
    let zoom: number;
    if (!saved || saved === "page-fit") {
      zoom = computeDiffFitPage(iframe);
    } else if (saved === "page-width") {
      zoom = computeDiffFitWidth(iframe);
    } else {
      const parsed = parseFloat(saved);
      zoom = Number.isFinite(parsed) ? parsed : computeDiffFitPage(iframe);
    }

    applyDiffZoom(iframe, zoom);
    onZoomChange?.(zoom);
  }, [iframeRef, onZoomChange]);

  if (!resolvedVersionId) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        {t("pdfToolbar.diffUnavailable")}
      </div>
    );
  }

  if (cachedHtml === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="sm" className="text-muted-foreground" />
      </div>
    );
  }

  if (cachedHtml === null || cachedHtml.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        {t("pdfToolbar.diffUnavailable")}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title={t("pdfToolbar.viewDiff")}
      sandbox="allow-same-origin"
      srcDoc={cachedHtml}
      className="h-full w-full bg-muted/20"
      onLoad={handleLoad}
    />
  );
});
